import React, { useState, useEffect } from 'react';
import { 
  Clock, Filter, Plus, Search, Calendar, User, 
  Loader2, ChevronDown, ChevronUp, CheckCircle, AlertCircle,
  Phone, Mail, FileText, MessageSquare, X
} from 'lucide-react';
import { suiviConfigAPI } from '../services/api';

/**
 * Composant dynamique pour l'onglet Suivi
 * Affiche la timeline, les tâches et permet d'ajouter des actions
 */
const DynamicSuiviView = ({ dossierId, user, onUpdate }) => {
  const [suivis, setSuivis] = useState([]);
  const [taches, setTaches] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('timeline'); // timeline, taches
  
  // Filtres
  const [filters, setFilters] = useState({
    type_action: '',
    date_debut: '',
    date_fin: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  
  // Formulaires
  const [showAddAction, setShowAddAction] = useState(false);
  const [showAddTache, setShowAddTache] = useState(false);
  const [newAction, setNewAction] = useState({
    type_action: '',
    description: '',
    details: ''
  });
  const [newTache, setNewTache] = useState({
    titre: '',
    description: '',
    date_echeance: '',
    priorite: 'moyenne',
    type_action: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, [dossierId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [typesRes, suivisRes, tachesRes] = await Promise.all([
        suiviConfigAPI.getTypes(),
        suiviConfigAPI.getSuivis(dossierId, filters),
        suiviConfigAPI.getTaches(dossierId)
      ]);
      setTypes(typesRes.data);
      setSuivis(suivisRes.data);
      setTaches(tachesRes.data);
    } catch (error) {
      console.error('Erreur chargement:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = async () => {
    setLoading(true);
    try {
      const res = await suiviConfigAPI.getSuivis(dossierId, filters);
      setSuivis(res.data);
    } catch (error) {
      console.error('Erreur filtrage:', error);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setFilters({ type_action: '', date_debut: '', date_fin: '' });
    loadData();
  };

  const handleAddAction = async (e) => {
    e.preventDefault();
    if (!newAction.type_action || !newAction.description) return;
    
    setSaving(true);
    try {
      await suiviConfigAPI.createSuivi({
        dossier_id: dossierId,
        type_action: newAction.type_action,
        description: newAction.description,
        details: newAction.details,
        user_id: user?.id,
        user_nom: user ? `${user.prenom} ${user.nom}` : 'Système'
      });
      await loadData();
      setNewAction({ type_action: '', description: '', details: '' });
      setShowAddAction(false);
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Erreur création action:', error);
      alert('Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  const handleAddTache = async (e) => {
    e.preventDefault();
    if (!newTache.titre) return;
    
    setSaving(true);
    try {
      await suiviConfigAPI.createTache({
        dossier_id: dossierId,
        titre: newTache.titre,
        description: newTache.description,
        date_echeance: newTache.date_echeance || null,
        priorite: newTache.priorite,
        type_action: newTache.type_action || null,
        created_by_id: user?.id,
        created_by_nom: user ? `${user.prenom} ${user.nom}` : 'Système'
      });
      await loadData();
      setNewTache({ titre: '', description: '', date_echeance: '', priorite: 'moyenne', type_action: '' });
      setShowAddTache(false);
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Erreur création tâche:', error);
      alert('Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateTacheStatut = async (tacheId, newStatut) => {
    try {
      await suiviConfigAPI.updateTache(tacheId, { statut: newStatut });
      await loadData();
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Erreur mise à jour:', error);
    }
  };

  const getTypeInfo = (code) => {
    const type = types.find(t => t.code === code);
    return type || { icone: '📌', couleur: 'bg-gray-500', label: code };
  };

  if (loading && suivis.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-green-600" />
      </div>
    );
  }

  const tachesEnCours = taches.filter(t => t.statut !== 'termine' && t.statut !== 'annule');
  const tachesTerminees = taches.filter(t => t.statut === 'termine');

  return (
    <div className="space-y-4">
      {/* Header avec onglets et actions */}
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          <button
            onClick={() => setActiveView('timeline')}
            className={`px-4 py-2 rounded-lg font-medium transition ${
              activeView === 'timeline' 
                ? 'bg-green-600 text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            📊 Timeline ({suivis.length})
          </button>
          <button
            onClick={() => setActiveView('taches')}
            className={`px-4 py-2 rounded-lg font-medium transition ${
              activeView === 'taches' 
                ? 'bg-green-600 text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            📌 Tâches ({tachesEnCours.length})
          </button>
        </div>
        
        <div className="flex gap-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`px-3 py-2 rounded-lg flex items-center gap-2 transition ${
              showFilters || filters.type_action || filters.date_debut
                ? 'bg-blue-100 text-blue-700'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Filter className="w-4 h-4" />
            Filtrer
          </button>
          <button
            onClick={() => setShowAddAction(true)}
            className="px-3 py-2 bg-green-600 text-white rounded-lg flex items-center gap-2 hover:bg-green-700 transition"
          >
            <Plus className="w-4 h-4" />
            Action
          </button>
          <button
            onClick={() => setShowAddTache(true)}
            className="px-3 py-2 bg-purple-600 text-white rounded-lg flex items-center gap-2 hover:bg-purple-700 transition"
          >
            <Plus className="w-4 h-4" />
            Tâche
          </button>
        </div>
      </div>

      {/* Filtres */}
      {showFilters && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <div className="grid grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type d'action</label>
              <select
                value={filters.type_action}
                onChange={(e) => setFilters({ ...filters, type_action: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg text-sm"
              >
                <option value="">Tous les types</option>
                {types.map(t => (
                  <option key={t.code} value={t.code}>{t.icone} {t.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date début</label>
              <input
                type="date"
                value={filters.date_debut}
                onChange={(e) => setFilters({ ...filters, date_debut: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date fin</label>
              <input
                type="date"
                value={filters.date_fin}
                onChange={(e) => setFilters({ ...filters, date_fin: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg text-sm"
              />
            </div>
            <div className="flex items-end gap-2">
              <button
                onClick={applyFilters}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Appliquer
              </button>
              <button
                onClick={clearFilters}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Réinitialiser
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Vue Timeline */}
      {activeView === 'timeline' && (
        <div className="relative">
          {suivis.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Clock className="w-12 h-12 mx-auto mb-3 text-gray-400" />
              <p>Aucune action enregistrée</p>
              <button
                onClick={() => setShowAddAction(true)}
                className="mt-4 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                Ajouter la première action
              </button>
            </div>
          ) : (
            <>
              {/* Ligne verticale */}
              <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gradient-to-b from-green-400 via-blue-400 to-purple-400"></div>
              
              <div className="space-y-4">
                {suivis.map((suivi, index) => {
                  const typeInfo = getTypeInfo(suivi.type_action);
                  const isFirst = index === 0;
                  
                  return (
                    <div key={suivi.id} className="relative flex gap-4 ml-0">
                      {/* Point */}
                      <div className={`relative z-10 flex-shrink-0 w-12 h-12 rounded-full ${typeInfo.couleur} flex items-center justify-center text-xl text-white shadow-lg ${isFirst ? 'ring-4 ring-green-200' : ''}`}>
                        {typeInfo.icone}
                      </div>
                      
                      {/* Contenu */}
                      <div className="flex-1 pb-2">
                        <div className={`bg-white border rounded-xl p-4 shadow-sm hover:shadow-md transition ${isFirst ? 'border-green-300 bg-green-50' : 'border-gray-200'}`}>
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-xs font-medium text-gray-500 uppercase">
                                  {typeInfo.label}
                                </span>
                              </div>
                              <p className="font-semibold text-gray-900">{suivi.description}</p>
                              {suivi.details && (
                                <p className="text-sm text-gray-600 mt-1">{suivi.details}</p>
                              )}
                              <div className="flex items-center gap-2 mt-2">
                                <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-xs font-semibold">
                                  {suivi.user_nom?.split(' ').map(n => n[0]).join('') || '?'}
                                </div>
                                <span className="text-sm text-gray-600">{suivi.user_nom || 'Système'}</span>
                              </div>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <div className="flex items-center gap-1 text-gray-500">
                                <Clock className="w-4 h-4" />
                                <span className="text-sm font-medium">
                                  {new Date(suivi.date_action).toLocaleDateString('fr-FR')}
                                </span>
                              </div>
                              <span className="text-xs text-gray-400">
                                {new Date(suivi.date_action).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}

      {/* Vue Tâches */}
      {activeView === 'taches' && (
        <div className="space-y-4">
          {/* Tâches en cours */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-orange-500" />
              À faire ({tachesEnCours.length})
            </h3>
            {tachesEnCours.length === 0 ? (
              <p className="text-gray-500 text-center py-8">Aucune tâche en cours</p>
            ) : (
              <div className="space-y-2">
                {tachesEnCours.map(tache => (
                  <TacheCard 
                    key={tache.id} 
                    tache={tache} 
                    types={types}
                    onUpdateStatut={handleUpdateTacheStatut}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Tâches terminées */}
          {tachesTerminees.length > 0 && (
            <div>
              <h3 className="font-semibold text-gray-500 mb-3 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-500" />
                Terminées ({tachesTerminees.length})
              </h3>
              <div className="space-y-2 opacity-60">
                {tachesTerminees.slice(0, 5).map(tache => (
                  <TacheCard 
                    key={tache.id} 
                    tache={tache}
                    types={types}
                    onUpdateStatut={handleUpdateTacheStatut}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modal Ajouter Action */}
      {showAddAction && (
        <Modal onClose={() => setShowAddAction(false)} title="Ajouter une action">
          <form onSubmit={handleAddAction} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type d'action *</label>
              <select
                value={newAction.type_action}
                onChange={(e) => setNewAction({ ...newAction, type_action: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
                required
              >
                <option value="">Sélectionner...</option>
                {types.filter(t => !t.systeme).map(t => (
                  <option key={t.code} value={t.code}>{t.icone} {t.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
              <input
                type="text"
                value={newAction.description}
                onChange={(e) => setNewAction({ ...newAction, description: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="Ex: Appel au bénéficiaire pour demande de documents"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Détails (optionnel)</label>
              <textarea
                value={newAction.details}
                onChange={(e) => setNewAction({ ...newAction, details: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
                rows={3}
                placeholder="Informations complémentaires..."
              />
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setShowAddAction(false)} className="px-4 py-2 border rounded-lg">
                Annuler
              </button>
              <button 
                type="submit" 
                disabled={saving}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Ajouter
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Modal Ajouter Tâche */}
      {showAddTache && (
        <Modal onClose={() => setShowAddTache(false)} title="Créer une tâche">
          <form onSubmit={handleAddTache} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Titre *</label>
              <input
                type="text"
                value={newTache.titre}
                onChange={(e) => setNewTache({ ...newTache, titre: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="Ex: Relancer le bénéficiaire"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={newTache.description}
                onChange={(e) => setNewTache({ ...newTache, description: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
                rows={2}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Échéance</label>
                <input
                  type="datetime-local"
                  value={newTache.date_echeance}
                  onChange={(e) => setNewTache({ ...newTache, date_echeance: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Priorité</label>
                <select
                  value={newTache.priorite}
                  onChange={(e) => setNewTache({ ...newTache, priorite: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="basse">🟢 Basse</option>
                  <option value="moyenne">🟠 Moyenne</option>
                  <option value="haute">🔴 Haute</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type d'action lié</label>
              <select
                value={newTache.type_action}
                onChange={(e) => setNewTache({ ...newTache, type_action: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="">Aucun</option>
                {types.filter(t => !t.systeme).map(t => (
                  <option key={t.code} value={t.code}>{t.icone} {t.label}</option>
                ))}
              </select>
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setShowAddTache(false)} className="px-4 py-2 border rounded-lg">
                Annuler
              </button>
              <button 
                type="submit"
                disabled={saving}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Créer
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
};

// Composant Carte Tâche
const TacheCard = ({ tache, types, onUpdateStatut }) => {
  const prioriteColors = {
    haute: 'border-l-red-500 bg-red-50',
    moyenne: 'border-l-orange-500 bg-orange-50',
    basse: 'border-l-green-500 bg-green-50'
  };
  
  const statutOptions = [
    { value: 'a_faire', label: 'À faire', icon: '⏳' },
    { value: 'en_cours', label: 'En cours', icon: '🔄' },
    { value: 'termine', label: 'Terminé', icon: '✅' },
    { value: 'annule', label: 'Annulé', icon: '❌' }
  ];

  const isOverdue = tache.date_echeance && new Date(tache.date_echeance) < new Date() && tache.statut !== 'termine';

  return (
    <div className={`border-l-4 rounded-lg p-4 ${prioriteColors[tache.priorite]} ${tache.statut === 'termine' ? 'opacity-60' : ''}`}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <h4 className={`font-semibold ${tache.statut === 'termine' ? 'line-through text-gray-500' : 'text-gray-900'}`}>
            {tache.titre}
          </h4>
          {tache.description && (
            <p className="text-sm text-gray-600 mt-1">{tache.description}</p>
          )}
          <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
            {tache.date_echeance && (
              <span className={`flex items-center gap-1 ${isOverdue ? 'text-red-600 font-semibold' : ''}`}>
                <Calendar className="w-3 h-3" />
                {new Date(tache.date_echeance).toLocaleDateString('fr-FR')}
                {isOverdue && ' (en retard)'}
              </span>
            )}
            {tache.assigne_a_nom && (
              <span className="flex items-center gap-1">
                <User className="w-3 h-3" />
                {tache.assigne_a_nom}
              </span>
            )}
          </div>
        </div>
        
        <select
          value={tache.statut}
          onChange={(e) => onUpdateStatut(tache.id, e.target.value)}
          className="px-2 py-1 text-sm border rounded-lg bg-white"
        >
          {statutOptions.map(s => (
            <option key={s.value} value={s.value}>{s.icon} {s.label}</option>
          ))}
        </select>
      </div>
    </div>
  );
};

// Composant Modal
const Modal = ({ children, onClose, title }) => (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
    <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
      <div className="flex items-center justify-between p-4 border-b">
        <h3 className="text-lg font-bold text-gray-900">{title}</h3>
        <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
          <X className="w-5 h-5" />
        </button>
      </div>
      <div className="p-4">
        {children}
      </div>
    </div>
  </div>
);

export default DynamicSuiviView;
